import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { tr } from "../../i18n";
import "./guide.css";

/**
 * Kunskapsbank / Videoguider: how to use Notiz, one step per feature with a short clip.
 * The clips in public/guides/ were recorded on an invented demo customer ("Demo Fastigheter AB"),
 * so no real customer data is shown.
 */
const Ui = ({ children }) => <span className="guide-ui">{children}</span>;

const STEPS = [
  {
    id: "oversikt",
    title: "Översikten och menyn",
    video: "oversikt",
    poster: "oversikt-1",
    body: (
      <>
        <p>När du loggar in hamnar du på Översikt. Där ser du hur många skärmar, spellistor och filer ni har.</p>
        <ul>
          <li><Ui>Skärminställningar</Ui>: era skärmar och vad de visar.</li>
          <li><Ui>Spellistor</Ui>: i vilken ordning saker visas.</li>
          <li><Ui>Layout</Ui>: egna sidor med text, klocka, väder med mera.</li>
          <li><Ui>Integrationer (beta)</Ui>: data från andra system.</li>
          <li><Ui>Innehåll</Ui>: bilder, filmer, PDF och länkar.</li>
          <li><Ui>Användare</Ui>: vem som får logga in.</li>
        </ul>
      </>
    ),
  },
  {
    id: "innehall",
    title: "Ladda upp innehåll",
    video: "innehall",
    poster: "innehall-2",
    body: (
      <>
        <ol>
          <li>Gå till <Ui>Innehåll</Ui>.</li>
          <li>Klicka på <Ui>Lägg till innehåll</Ui>.</li>
          <li>Välj <Ui>Bild</Ui>, <Ui>Video</Ui>, <Ui>URL</Ui> eller <Ui>PDF</Ui> och välj filen.</li>
        </ol>
        <p>Du kan också dra och släppa filer rakt i rutan.</p>
        <p className="guide-tip">Gör bilder i 1920 × 1080 pixlar för liggande skärmar. Då fyller de skärmen utan kanter.</p>
      </>
    ),
  },
  {
    id: "spellista",
    title: "Gör en spellista",
    video: "spellista",
    poster: "spellista-2",
    body: (
      <>
        <ol>
          <li>Gå till <Ui>Spellistor</Ui> och klicka på <Ui>Skapa spellista</Ui>.</li>
          <li>Ge den ett namn, till exempel skärmens plats, och klicka på <Ui>Skapa</Ui>.</li>
          <li>Öppna spellistan. Klicka på <Ui>+</Ui> på en bild i mediabiblioteket, eller dra bilden till vänster.</li>
          <li>Dra i listan för att ändra ordning. Tiden på varje rad visar hur länge den visas.</li>
        </ol>
        <p>Uppe till höger väljer du <Ui>Övergång</Ui> och <Ui>Skalning</Ui> (hur bilder fyller skärmen).</p>
      </>
    ),
  },
  {
    id: "skarm",
    title: "Koppla en skärm",
    video: "skarm",
    poster: "skarm-1",
    extra: [
      { src: "skarm-kod", caption: "Före: skärmen visar sin kod." },
      { src: "skarm-spelar", caption: "Efter: skärmen spelar spellistan." },
    ],
    body: (
      <>
        <ol>
          <li>Starta skärmen. Den visar en kod med fyra siffror.</li>
          <li>Gå till <Ui>Skärminställningar</Ui> och klicka på <Ui>Lägg till skärm</Ui>.</li>
          <li>Skriv in koden, ett namn, orientering och plats. Klicka på <Ui>Spara</Ui>.</li>
          <li>Öppna spellistan, klicka på <Ui>Tilldela skärmar</Ui>, bocka i skärmen och bekräfta.</li>
        </ol>
        <p>Skärmen börjar visa spellistan inom några sekunder.</p>
        <p className="guide-tip">En spellista kan visas på många skärmar samtidigt. Ändrar du i spellistan uppdateras alla.</p>
      </>
    ),
  },
  {
    id: "layout",
    title: "Gör en layout",
    video: "layout",
    poster: "layout-2",
    body: (
      <>
        <p>En layout är en egen sida som du bygger ihop själv, till exempel en välkomstskärm med väder.</p>
        <ol>
          <li>Gå till <Ui>Layout</Ui> och klicka på <Ui>Skapa layout</Ui>.</li>
          <li>Skriv ett namn uppe till vänster.</li>
          <li>Under <Ui>Media</Ui> klickar du på en bild för att lägga in den. Dra i hörnet för att ändra storlek.</li>
          <li>Under <Ui>Appar</Ui> finns klocka, väder, avgångar och integrationer. Välj en stil och dra den dit du vill.</li>
          <li>Klicka på det du lagt in för att ändra det i panelen till höger, till exempel ort för vädret.</li>
          <li>Klicka på <Ui>Spara</Ui>. Layouten kan nu läggas i en spellista.</li>
        </ol>
      </>
    ),
  },
  {
    id: "integrationer",
    title: "Integrationer (beta)",
    video: "integrationer",
    poster: "integrationer-3",
    body: (
      <>
        <p>Här kopplar ni in information från andra system, till exempel nyheter, väder eller avgångar. Kort som står <b>Kommer snart</b> går inte att ansluta än.</p>
        <ol>
          <li>Gå till <Ui>Integrationer (beta)</Ui> och klicka på <Ui>Anslut</Ui>.</li>
          <li>Öppna en layout. Under <Ui>Appar</Ui> väljer du <Ui>Integrationswidget</Ui>.</li>
          <li>Klicka på widgeten. Välj integration till höger och fyll i det som behövs, till exempel adressen till ett nyhetsflöde.</li>
          <li>Spara layouten.</li>
        </ol>
        <p className="guide-tip">Skärmarna hämtar ny data varje minut. Om källan ligger nere visas det som hämtades senast.</p>
      </>
    ),
  },
  {
    id: "anvandare",
    title: "Bjud in kollegor",
    video: "anvandare",
    poster: "anvandare-3",
    body: (
      <>
        <ol>
          <li>Gå till <Ui>Användare</Ui> och klicka på <Ui>Lägg till användare</Ui>.</li>
          <li>Fyll i namn, e-post och ett lösenord, och välj roll.</li>
          <li>Kollegan får ett mejl med en länk där hen kan välja ett eget lösenord.</li>
        </ol>
        <dl className="guide-roles">
          <dt>Admin</dt><dd>Kan ändra allt och bjuda in fler.</dd>
          <dt>Användare</dt><dd>Kan ändra innehåll, spellistor och layouter.</dd>
          <dt>Visning</dt><dd>Kan bara titta, inte ändra.</dd>
        </dl>
        <p>Med <Ui>Tilldela</Ui> kan du begränsa vilka skärmar en person får hantera.</p>
      </>
    ),
  },
];

export default function Guide({ user }) {
  const { hash } = useLocation();

  // Dashboard links can point at one step (/guide#skarm).
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <Layout title={tr("Knowledge base")} user={user}>
      <div className="guide-page">
        <header className="guide-hero">
          <h1>Kom igång med Notiz</h1>
          <p>Från första bilden till en skärm som visar den. Varje steg har ett kort klipp som visar hur det går till.</p>
          <p className="guide-note">Klippen är inspelade på en påhittad demokund. Inga riktiga kunduppgifter visas.</p>
        </header>

        <nav className="guide-toc" aria-label="Steg">
          <ol>
            {STEPS.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" }); }}>
                  <span className="guide-toc-n">{i + 1}</span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {STEPS.map((s, i) => (
          <section key={s.id} id={s.id} className="guide-step">
            <div className="guide-step-text">
              <span className="guide-step-num">Steg {i + 1}</span>
              <h2>{s.title}</h2>
              {s.body}
            </div>
            <div className="guide-media">
              <video
                src={`/guides/${s.video}.mp4`}
                poster={`/guides/${s.poster}.jpg`}
                controls
                muted
                playsInline
                preload="none"
                aria-label={`Klipp: ${s.title}`}
              />
              {s.extra && (
                <div className="guide-pair">
                  {s.extra.map((x) => (
                    <figure key={x.src}>
                      <img src={`/guides/${x.src}.jpg`} alt={x.caption} loading="lazy" />
                      <figcaption>{x.caption}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    </Layout>
  );
}
